/**
 * Bounded Context: Presence — Pinia store
 *
 * Mantém o estado de presença observável do aprendiz.
 * Em v0.1: dados in-memory (sem persistência local). Pyodide bridge em HLOS-7.
 *
 * HLOS-16 — Agora consome `PresenceManager` (espelho de hlos.kernel.runtime.presence).
 * Mantém API HLOS-2 (compat) e adiciona `managerRef` para consumers que precisam
 * do estado engine-level (ex: HLOS-17 context cap R6).
 *
 * Constitucional:
 * - Princípio V (Aprendiz Decide): o estado `away`/`dnd` é sinalizado pelo usuário,
 *   nunca inferido agressivamente (CC-04).
 * - CC-01..08: zero contador/timer visível ao usuário.
 */

import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";
import type { Presence, PresenceSignal, PresenceState } from "./types";
import { PresenceManager, mapUiSignalToEngine } from "./manager";

export const usePresenceStore = defineStore("presence", () => {
  const state = ref<PresenceState>("active");
  const updatedAt = ref<number>(Date.now());
  const lastSignal = ref<PresenceSignal>("user_request");

  /**
   * HLOS-16: engine interno. `shallowRef` porque PresenceManager é
   * mutável in-place (não trocamos a instância). Consumers externos
   * acessam via `managerRef.value.isPresent()` (R6 gate) ou `.state`.
   */
  const managerRef = shallowRef(new PresenceManager());

  /**
   * Aplica um sinal de presença. Mantém API HLOS-2 (compat).
   * Bônus HLOS-16: atualiza também o manager interno (estado engine).
   */
  function applySignal(signal: PresenceSignal, next: PresenceState): void {
    state.value = next;
    updatedAt.value = Date.now();
    lastSignal.value = signal;
    managerRef.value.update(mapUiSignalToEngine(signal, next));
  }

  /** View readonly para consumidores (componentes/composables). */
  const view = (): Presence => ({
    state: state.value,
    updatedAt: updatedAt.value,
    lastSignal: lastSignal.value,
  });

  return { state, updatedAt, lastSignal, managerRef, applySignal, view };
});
